import React, { useEffect, useMemo, useRef, useState } from 'react';
import { constructMediaUrl } from '../../../api/helpers';
import NavigationControls from './NavigationControls';

interface ImageGalleryProps {
  urls: string[];
  currentIndex: number;
  setCurrentIndex: (i: number) => void;
}

const MAX_RETRIES = 4;
const RETRY_DELAYS = [1000, 2000, 4000, 6000];

const ImageGallery: React.FC<ImageGalleryProps> = ({
  urls,
  currentIndex,
  setCurrentIndex,
}) => {
  const [retryCount, setRetryCount] = useState(0);
  const [hasFailed, setHasFailed] = useState(false);
  const retryTimerRef = useRef<ReturnType<typeof setTimeout>>();
  const activeUrl = urls[currentIndex] || '';

  useEffect(() => {
    setRetryCount(0);
    setHasFailed(false);
    if (retryTimerRef.current) clearTimeout(retryTimerRef.current);
  }, [activeUrl]);

  useEffect(() => {
    return () => {
      if (retryTimerRef.current) clearTimeout(retryTimerRef.current);
    };
  }, []);

  const src = useMemo(() => {
    const base = constructMediaUrl(activeUrl);
    if (!base) return '';
    if (retryCount === 0) return base;
    return `${base}${base.includes('?') ? '&' : '?'}t=${Date.now()}`;
  }, [activeUrl, retryCount]);

  const handleLoad = () => {
    setRetryCount(0);
    setHasFailed(false);
    if (retryTimerRef.current) clearTimeout(retryTimerRef.current);
  };

  const handleError = () => {
    if (retryCount < MAX_RETRIES) {
      const delay = RETRY_DELAYS[retryCount] ?? 6000;
      retryTimerRef.current = setTimeout(() => {
        setRetryCount((c) => c + 1);
      }, delay);
    } else {
      setHasFailed(true);
      console.error('Image failed to load after retries:', activeUrl);
    }
  };

  return (
    <div>
      <div className="relative aspect-[4/5] max-h-[500px] bg-black rounded-lg overflow-hidden border border-white/5">
        {hasFailed ? (
          <div className="w-full h-full flex items-center justify-center text-slate-500 text-sm">
            Failed to load image
          </div>
        ) : (
          <img
            key={`${activeUrl}-${retryCount}`}
            src={src}
            alt=""
            className="w-full h-full object-contain"
            loading="lazy"
            decoding="async"
            onLoad={handleLoad}
            onError={handleError}
          />
        )}
      </div>
      {urls.length > 1 && (
        <NavigationControls
          currentIndex={currentIndex}
          total={urls.length}
          onPrev={() => setCurrentIndex(Math.max(0, currentIndex - 1))}
          onNext={() => setCurrentIndex(Math.min(urls.length - 1, currentIndex + 1))}
        />
      )}
    </div>
  );
};

export default ImageGallery;
